import {
  descriptorControlField,
  doodBlockConfig,
  paramRef,
  propertyValue,
  scenarioBuilder,
} from './dood-block-config';
import type { SlotDefinition } from './bloc-definition';

/**
 * Migration proof: `AnchorBlock` re-declared through `doodBlockConfig`.
 *
 * The resulting `anchorBlockDescriptor` is structurally compatible with the
 * legacy dashboard `IBlockDescriptor` (asserted in `anchor-block.example.spec.ts`),
 * so the dashboard can later swap `AnchorBlock` for `anchorBlockDescriptor`
 * without changing the persisted data shape or the webapp runtime. The CB2
 * projection `anchorBlockBlocDefinition` is asserted assignable to
 * `BlocDefinition` in `anchor-block.bloc-definition.compatibility.spec.ts`.
 */
const anchorSlots: readonly SlotDefinition[] = [
  {
    key: 'nav_items',
    name: 'pages.blocks.anchor.nav-items.label',
    accept: 'whitelist',
    allowedTypes: ['AnchorLinkBlock', 'AnchorDropdownBlock'],
    slotItemFormat: 'ids',
  },
];

export const {
  anchorBlockDescriptor,
  anchorBlockBlocDefinition,
  composeAnchorConfig,
  anchorBlockDashboardLayout,
  anchorBlockDefinitionSetupScenarios,
  anchorBlockConfig,
  anchorBlockProperties,
  anchorBlockResolveConfig,
} = doodBlockConfig(
  {
    name: 'Anchor',
    componentType: 'AnchorBlockComponent',
    category: 'navigation',
    description: "En-tête de navigation collant avec logo et items de menu.",
    keywords: ['en-tête', 'header', 'logo', 'menu', 'navigation', 'barre'],
    slots: anchorSlots,
    sharedFields: {
      props: [
        { name: 'name', type: 'text' },
        { name: 'colors.backgroundColor', type: 'color' },
        { name: 'logo.label', type: 'translation' },
      ],
      descriptorControlFields: [
        { name: 'showMobileOptions', type: 'checkbox' },
      ],
    },
  },

  shared => ({
    name: propertyValue({
      type: shared.props.name,
      value: 'anchor',
      options: { label: 'pages.blocks.anchor.name.label' },
    }),
    colors: {
      backgroundColor: propertyValue({
        type: shared.props.colors.backgroundColor,
        value: { type: 'palette', value: 'neutral-50' },
        description: 'Default background color',
        options: { label: 'pages.blocks.anchor.background-color.label' },
      }),
      textColor: propertyValue({
        type: 'color',
        value: { type: 'palette', value: 'neutral-900' },
        description: 'Default text color',
      }),
    },
    logo: {
      // Multi-locale merchant label: projects to `string` + `$cases(locale)` in CB2.
      label: propertyValue({
        type: shared.props.logo.label,
        value: { fr: 'Accueil', en: 'Home' },
        options: { label: 'pages.blocks.anchor.logo.label' },
      }),
      // `$param` reference resolved at runtime against the journey context.
      alt: propertyValue({
        type: 'text',
        value: paramRef('brand.name'),
        options: { label: 'pages.blocks.anchor.logo.alt.label' },
      }),
    },
    stickyOnMobile: propertyValue(
      {
        type: 'checkbox',
        description: 'Enable sticky navigation on mobile (declarative condition).',
        options: { label: 'pages.blocks.anchor.sticky-on-mobile.label' },
      },
      [
        {
          condition: { type: 'device', options: { in: ['mobile'] } },
          value: true,
        },
        {
          condition: { type: 'device', options: { in: ['desktop', 'kiosk'] } },
          value: false,
        },
      ],
    ),
    showMobileOptions: descriptorControlField({
      type: shared.descriptorControlFields.showMobileOptions,
      value: false,
      options: { label: 'pages.blocks.anchor.show-mobile-options.label' },
    }),
  }),

  anchorBlockDashboardLayoutFactory
);

function anchorBlockDashboardLayoutFactory() {
  return {
    omitFields: ['name'],
    tabs: [
      {
        label: 'Design',
        fieldsets: [
          {
            legend: 'Colors',
            fields: [
              'colors.backgroundColor',
              {
                prop: 'colors.textColor',
                label: 'pages.blocks.anchor.text-color.label',
              },
            ],
          },
          {
            legend: 'Logo',
            fields: [
              'logo.label',
              'logo.alt',
            ],
          },
          {
            legend: 'Mobile',
            fields: [
              {
                descriptorControlField: 'showMobileOptions',
                label: 'pages.blocks.anchor.show-mobile-options.label',
              },
              {
                prop: 'stickyOnMobile',
                label: 'pages.blocks.anchor.sticky-on-mobile.label',
              },
            ],
          },
        ],
      },
    ],
  };
}

function anchorBlockScenarioFactory(_ctx: import('./dood-config-core').DoodBlockScenarioContext) {
  return [
    [
      scenarioBuilder.describe('pages.blocks.anchor.preview-scenario.default.name', {
        _devDescription: 'Navigation anchor rendered with its default name and colors.',
      }),
      scenarioBuilder.describe('pages.blocks.anchor.preview-scenario.mobile-sticky.name', {
        _devDescription: 'Navigation anchor rendered with sticky mobile navigation enabled.',
        when: 'pages.blocks.anchor.preview-scenario.mobile-sticky.when',
      }),
    ],
  ];
}

export const anchorBlockPreviewScenarios =
  anchorBlockDefinitionSetupScenarios(anchorBlockScenarioFactory);

export const anchorBlockScenarios = anchorBlockPreviewScenarios.factory;
